// Plain-language presentation for the Market calendar admin tab: months,
// money, entry states, job runs and regeneration outcomes. The server is the
// authority for every rule shown here (completed months only, the spend caps,
// two verified events to publish); this module only words its answers.

// The bundled backfill starts in January 1995.
export const FIRST_YEAR = 1995;
export const MONTH_NAMES = Object.freeze([
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]);
const SHORT_MONTHS = MONTH_NAMES.map((name) => name.slice(0, 3));

export const pad = (value) => String(value).padStart(2, '0');

// The newest month the server lets an admin regenerate: the last whole UTC month.
export function latestCompletedMonth(now = new Date()) {
  const month = now.getUTCMonth();
  return month === 0 ? `${now.getUTCFullYear() - 1}-12` : `${now.getUTCFullYear()}-${pad(month)}`;
}

// "2020-03" (or "2020-03-01") as "March 2020".
export function monthLabel(value) {
  const match = /^(\d{4})-(\d{2})/.exec(String(value || ''));
  return match ? `${MONTH_NAMES[Number(match[2]) - 1]} ${match[1]}` : '';
}

// "2020-03-01" as "Mar 2020", for the runs table.
export function shortMonthLabel(value) {
  const match = /^(\d{4})-(\d{2})/.exec(String(value || ''));
  return match ? `${SHORT_MONTHS[Number(match[2]) - 1]} ${match[1]}` : '–';
}

// "2020-03-02" as "2 Mar".
export function dayLabel(value) {
  const match = /^\d{4}-(\d{2})-(\d{2})/.exec(String(value || ''));
  return match ? `${Number(match[2])} ${SHORT_MONTHS[Number(match[1]) - 1]}` : '';
}

export function dateRangeLabel(start, end) {
  return start === end || !end ? dayLabel(start) : `${dayLabel(start)} – ${dayLabel(end)}`;
}

// The month's first and last days: the entries route's from and to.
export function monthBounds(month) {
  const [year, index] = month.split('-').map(Number);
  const lastDay = new Date(Date.UTC(year, index, 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${pad(lastDay)}` };
}

export function formatUsd(value) {
  const amount = Number(value);
  if (value === null || value === undefined || value === '' || !Number.isFinite(amount)) return '–';
  if (amount > 0 && amount < 0.005) return 'under $0.01';
  return `$${amount.toFixed(2)}`;
}

// What is left under the monthly and all-time caps, from the job-runs spend summary.
export function remainingBudget(spend) {
  const line = (spent, cap) => {
    const used = Number(spent);
    const limit = Number(cap);
    return Number.isFinite(used) && Number.isFinite(limit)
      ? { spent: used, cap: limit, left: Math.max(limit - used, 0) }
      : null;
  };
  const month = line(spend?.month_usd, spend?.caps?.month);
  const total = line(spend?.total_usd, spend?.caps?.total);
  const run = Number(spend?.caps?.run);
  return month && total && Number.isFinite(run) ? { run, month, total } : null;
}

const CAP_NAMES = Object.freeze({
  run: 'per-run cap',
  month: 'monthly AI budget',
  total: 'all-time AI budget',
});

const RUN_ERRORS = Object.freeze({
  ai_key_not_configured: {
    short: 'OpenAI key not configured',
    text: 'This environment isn\'t configured for an OpenAI key, so nothing was drafted.',
  },
  ai_key_unavailable: {
    short: 'No usable OpenAI key',
    text: 'Couldn\'t read a usable OpenAI key. If none is stored yet, add one under System Credentials; '
      + 'otherwise try again in a few minutes.',
    needsKey: true,
  },
  draft_unusable: {
    short: 'OpenAI\'s draft was unreadable',
    text: 'OpenAI\'s draft was incomplete or unreadable. Try again.',
  },
  draft_rejected: {
    short: 'OpenAI\'s draft didn\'t fit the format',
    text: 'OpenAI\'s draft didn\'t fit the calendar format. Try again.',
  },
  verification_unavailable: {
    short: 'Cited pages couldn\'t be reached',
    text: 'Some cited pages couldn\'t be reached, so the draft couldn\'t be checked. Try again later.',
  },
  price_unknown: {
    short: 'No price for the AI model',
    text: 'The AI model has no price configured for this environment, so nothing was drafted.',
  },
  model_not_allowed: {
    short: 'AI model not allowed',
    text: 'The configured AI model isn\'t on the allowed list, so nothing was drafted.',
  },
  price_table_invalid: {
    short: 'AI price table invalid',
    text: 'The AI price table for this environment is invalid, so nothing was drafted.',
  },
  internal_error: {
    short: 'Server error',
    text: 'Something went wrong on the server.',
  },
});

function providerError(message) {
  const status = Number(/status (\d{3})/.exec(message)?.[1]);
  if (status === 401 || status === 403) {
    return {
      short: 'OpenAI rejected the key',
      text: 'OpenAI rejected the key. Test or replace it under System Credentials.',
      needsKey: true,
    };
  }
  if (status === 429) {
    return {
      short: 'OpenAI rate limit or quota',
      text: 'OpenAI is rate limiting this key, or its quota is used up. Try again later.',
    };
  }
  if (status === 504) return { short: 'OpenAI timed out', text: 'OpenAI didn\'t answer in time. Try again.' };
  return {
    short: 'OpenAI error',
    text: `OpenAI couldn't complete the draft${status ? ` (status ${status})` : ''}.${!status || status >= 500 ? ' Try again later.' : ''}`,
  };
}

// A run's recorded error ("code: message") in plain words: a short form for the
// runs table and a sentence for the result.
export function runErrorText(error) {
  const text = String(error || '');
  const separator = text.indexOf(': ');
  const code = separator > 0 ? text.slice(0, separator) : text;
  const message = separator > 0 ? text.slice(separator + 2) : '';
  if (code === 'provider_error') return providerError(message);
  const raw = message || code;
  return RUN_ERRORS[code] || { short: raw || 'Failed', text: raw || 'The run failed.' };
}

const verifiedPhrase = (counts) => `${counts.verified_events ?? 0} of ${counts.events ?? 0} events verified`;

// The answer to "Regenerate month", in plain words.
export function describeRegeneration(result, month) {
  const name = monthLabel(month);
  const counts = result?.counts || {};
  const spent = Number(result?.spend_usd) || 0;
  const cost = ` Cost: ${formatUsd(spent)}.`;
  if (result?.status === 'succeeded') {
    if (result.entry_status === 'published') {
      const version = Number(counts.entry_version) || null;
      return {
        severity: 'success',
        text: `Done. ${name} now shows the new version${version ? ` (version ${version})` : ''}, with `
          + `${verifiedPhrase(counts)}.${version > 1 ? ' The previous version is kept as history.' : ''}${cost}`,
      };
    }
    if (result.entry_status === 'kept_current') {
      return {
        severity: 'warning',
        text: `Nothing changed. Only ${verifiedPhrase(counts)}, and 2 are needed to publish, so ${name} `
          + `keeps its current version.${cost}`,
      };
    }
    if (result.entry_status === 'hidden') {
      return {
        severity: 'warning',
        text: `Not published. Only ${verifiedPhrase(counts)}, and 2 are needed, so the new version is `
          + `stored hidden and nothing is shown for ${name}.${cost}`,
      };
    }
    return { severity: 'success', text: `Done.${cost}` };
  }
  if (result?.status === 'capped') {
    const cap = counts.cap_usd ? `${formatUsd(counts.cap_usd)} ` : '';
    return {
      severity: 'error',
      text: `Not regenerated. This run needs up to ${formatUsd(counts.estimate_usd)}, which would pass the `
        + `${cap}${CAP_NAMES[result.cap_hit] || 'spend cap'}. Nothing was charged.`
        + `${result.cap_hit === 'month' ? ' The monthly budget starts again on the 1st (UTC).' : ''}`,
    };
  }
  if (result?.status === 'failed') {
    const reason = runErrorText(result.error);
    return {
      severity: 'error',
      text: `Not regenerated. ${reason.text}`
        + `${spent > 0 ? ` This run used ${formatUsd(spent)} of the AI budget.` : ' Nothing was charged.'}`,
      needsKey: Boolean(reason.needsKey),
    };
  }
  if (result?.status === 'superseded') {
    return { severity: 'warning', text: 'Another attempt took over this run. Its result shows under Recent runs.' };
  }
  return { severity: 'info', text: 'The run ended without a result. Check Recent runs.' };
}

// The request itself failed or got no answer.
export function describeRegenerationError(error, month) {
  if (error?.status === 409) {
    return {
      severity: 'warning',
      text: `${monthLabel(month)} is already being drafted by another run. Wait for it to finish under `
        + 'Recent runs, then try again.',
    };
  }
  if (error?.status >= 400 && error?.status < 500) {
    return { severity: 'error', text: `Not regenerated. ${error.message}` };
  }
  // A dropped connection or a gateway timeout can leave the run going on the server.
  return {
    severity: 'warning',
    text: 'The server didn\'t confirm the result, so the run may still be going. Check Recent runs in a few '
      + 'minutes before trying again.',
  };
}

// How far a running regeneration has got, read from its job-run row.
export function runPhase(run) {
  if (run?.counts?.entry_id != null) return 'checking factor returns for stress periods';
  if (Number(run?.reserved_usd) > 0) return 'drafting with OpenAI and web search';
  if (Number(run?.spend_usd) > 0) return 'checking every cited page';
  return 'starting';
}

export const RUN_KINDS = Object.freeze({
  regenerate: 'Regenerate',
  monthly: 'Monthly job',
  seed_load: 'Seed load',
  link_check: 'Link check',
});

export const RUN_STATUSES = Object.freeze({
  running: { label: 'Running', color: 'info' },
  succeeded: { label: 'Succeeded', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
  capped: { label: 'Capped', color: 'warning' },
  skipped: { label: 'Skipped', color: 'default' },
});

function entryOutcome(counts) {
  const verified = counts.events != null ? ` (${counts.verified_events ?? 0} of ${counts.events} verified)` : '';
  if (counts.entry_status === 'published') {
    return `Published${counts.entry_version ? ` version ${counts.entry_version}` : ''}${verified}`;
  }
  if (counts.entry_status === 'kept_current') return `Kept the current version${verified}`;
  if (counts.entry_status === 'hidden') return `Stored hidden${verified}`;
  return 'Done';
}

// A deployed instance's automatic load of the bundled seed files, written after its files.
function describeStartupLoad(counts) {
  const sentences = [];
  const loaded = counts.loaded?.length ?? 0;
  if (loaded) sentences.push(`Loaded ${loaded} bundled file${loaded === 1 ? '' : 's'} at startup`);
  const [first, ...others] = counts.errors || [];
  if (first) sentences.push(`${first.file} didn't load: ${first.problem}`);
  if (others.length) sentences.push(`${others.length} more file${others.length === 1 ? '' : 's'} didn't load`);
  const left = counts.deferred?.length ?? 0;
  if (left) sentences.push(`${left} wait${left === 1 ? 's' : ''} for the next start`);
  return sentences.join('. ') || 'Nothing to load at startup';
}

// One run's result for the Recent runs table.
export function describeRun(run) {
  const counts = run?.counts || {};
  if (run?.kind === 'seed_load' && String(run.run_key || '').startsWith('seed_autoload:')) {
    return describeStartupLoad(counts);
  }
  switch (run?.status) {
    case 'running': {
      const phase = runPhase(run);
      return `${phase[0].toUpperCase()}${phase.slice(1)}…`;
    }
    case 'succeeded':
      if (run.kind === 'seed_load') {
        return `${counts.seed_name} v${counts.seed_version}: ${counts.inserted ?? 0} new, `
          + `${counts.unchanged ?? 0} unchanged`;
      }
      if (run.kind === 'link_check') {
        return `${counts.checked ?? 0} links: ${counts.ok ?? 0} ok, ${counts.archived ?? 0} archived, `
          + `${counts.dead ?? 0} dead`;
      }
      return entryOutcome(counts);
    case 'skipped':
      return counts.reason === 'entry_exists' ? 'The month already had an entry' : 'Skipped';
    case 'capped':
      return `Stopped by the ${CAP_NAMES[run.cap_hit] || 'spend cap'}`;
    case 'failed':
      return runErrorText(run.error).short;
    default:
      return '';
  }
}

// What a run cost so far: money held for an OpenAI call in flight counts too.
export function runCost(run) {
  const reserved = Number(run?.reserved_usd) || 0;
  return reserved > 0 ? `${formatUsd(reserved)} held` : formatUsd(run?.spend_usd ?? 0);
}

export function entryStatus(entry) {
  if (entry.status === 'published') return { label: 'Published', color: 'success' };
  if (entry.status === 'superseded') return { label: 'Replaced', color: 'default' };
  if (entry.hidden_reason === 'admin') return { label: 'Hidden by an admin', color: 'warning' };
  return { label: 'Hidden: under 2 verified events', color: 'warning' };
}

export function entryOrigin(entry) {
  const model = entry.model ? ` with ${entry.model}` : '';
  if (entry.origin === 'seed') return `Seed file ${entry.seed_name} v${entry.seed_version}`;
  if (entry.origin === 'monthly') return `Monthly job${model}`;
  if (entry.origin === 'regenerate') return `Regenerated${model}`;
  return entry.origin || '';
}

export function periodLabel(entry) {
  return entry.granularity === 'week' ? `Week of ${dayLabel(entry.period_start)}` : 'Month';
}

// One group per period: the month first, then its weeks in date order. Each
// group holds its current version (published, or hidden by the checks or an
// admin) and the versions it replaced, newest first.
export function groupEntries(items) {
  const groups = new Map();
  (Array.isArray(items) ? items : []).forEach((entry) => {
    const key = `${entry.granularity}:${entry.period_start}`;
    const group = groups.get(key) || {
      key, granularity: entry.granularity, periodStart: entry.period_start, current: null, earlier: [],
    };
    if (entry.status === 'superseded') group.earlier.push(entry);
    else group.current = entry;
    groups.set(key, group);
  });
  return [...groups.values()]
    .map((group) => ({ ...group, earlier: [...group.earlier].sort((a, b) => b.version - a.version) }))
    .sort((a, b) => {
      if (a.granularity !== b.granularity) return a.granularity === 'month' ? -1 : 1;
      return a.periodStart.localeCompare(b.periodStart);
    });
}
